import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { GridApiPro, GridCallbackDetails, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';

export const useBulkSelection = <SelectedRowType, ResponseType extends Record<string, SelectedRowType[]>>({
  fetchContents,
  dataKey,
  pageSize = DATA_GRID_LAZY_PAGE_SIZE,
  gridApi,
}: {
  fetchContents?: (parameters: {
    page: number;
    perPage: number;
  }) => Promise<ResponseType & { pageData: { total: number } }>;
  dataKey?: keyof ResponseType;
  pageSize?: number;
  gridApi?: React.RefObject<GridApiPro | null | undefined>;
} = {}) => {
  const [selectedRows, setSelectedRows] = useState<SelectedRowType[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel | undefined>();
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const handleSelectionChange = async (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => {
    setSelectionModel(rowSelectionModel);
    const isSelectAll = rowSelectionModel.type === 'exclude' && rowSelectionModel.ids.size === 0;

    if (isSelectAll && fetchContents && dataKey) {
      setIsLoadingAll(true);
      try {
        const allItems: SelectedRowType[] = [];
        const firstPageResponse = await fetchContents({ page: 1, perPage: pageSize });
        const totalCount = firstPageResponse.pageData.total;

        const firstPageItems = firstPageResponse[dataKey];
        allItems.push(...firstPageItems);

        if (totalCount > 0) {
          const totalPages = Math.ceil(totalCount / pageSize);

          for (let page = 2; page <= totalPages; page += 1) {
            const response = await fetchContents({ page, perPage: pageSize });
            const items = response[dataKey];
            allItems.push(...items);
          }
        }

        setSelectedRows(allItems);
      } finally {
        setIsLoadingAll(false);
      }
    } else if (rowSelectionModel.type === 'exclude') {
      const allRowIds = details.api.getAllRowIds();
      const excludedIds = new Set(rowSelectionModel.ids);

      const selectedItems = allRowIds
        .filter(id => !excludedIds.has(id))
        .map(id => details.api.getRow(id))
        .filter(Boolean);

      setSelectedRows(selectedItems);
      setIsLoadingAll(false);
    } else {
      const selectedItems = [...rowSelectionModel.ids].map(id => details.api.getRow(id)).filter(Boolean);
      setSelectedRows(selectedItems);
      setIsLoadingAll(false);
    }
  };

  const clearSelection = () => {
    setSelectedRows([]);
    setSelectionModel(undefined);
    setIsLoadingAll(false);
    gridApi?.current?.setRowSelectionModel({ type: 'include', ids: new Set() });
  };

  return {
    selectedRows,
    selectionModel,
    isLoadingAll,
    handleSelectionChange: (...arguments_: Parameters<typeof handleSelectionChange>) =>
      void handleSelectionChange(...arguments_),
    setSelectedRows,
    clearSelection,
  };
};
